import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Mail, Check, Radio, Calendar, Bell, Sparkles, ShieldCheck } from 'lucide-react';
import { ES } from '@/lib/translations';
import { useSeo, langFromPath } from '@/lib/seo';
import { STATION_HERO_IMAGE } from '@/lib/heroSlides';
import { mediaSrcSet } from '@/lib/media';

function SignupForm({ es, email, setEmail, submitting, done, onSubmit, onReset, t, dark, id, center }) {
  if (done) {
    return (
      <div className={`flex items-center gap-4 rounded-2xl p-5 ${dark ? 'bg-white/10 border border-white/15' : 'bg-white shadow-sm'}`}>
        <span className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Check size={24} className="text-white" />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-black text-lg ${dark ? 'text-white' : 'text-black'}`}>{t("You're subscribed!")}</p>
          <p className={`text-sm ${dark ? 'text-white/70' : 'text-neutral-500'}`}>{t('Watch your inbox for the next Yakaleo update.')}</p>
          <button onClick={onReset} className={`mt-1 text-sm font-bold hover:underline ${dark ? 'text-fuchsia-300' : 'text-purple-700'}`}>
            {t('Sign up another email')}
          </button>
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={onSubmit} className="w-full">
      <label htmlFor={id} className="sr-only">{t('Email Address')}</label>
      <div className={`flex flex-col sm:flex-row gap-2 rounded-2xl sm:rounded-full p-2 ${dark ? 'bg-white shadow-2xl shadow-purple-950/60' : 'bg-white shadow-sm border border-neutral-200'}`}>
        <div className="relative flex-1">
          <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            id={id}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder={es ? 'tu@email.com' : 'you@example.com'}
            className="w-full h-12 pl-11 pr-4 rounded-full bg-transparent text-black text-base placeholder:text-neutral-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="h-12 px-7 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white font-black text-sm tracking-widest inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
          {es ? 'SUSCRIBIRME' : 'SUBSCRIBE'}
        </button>
      </div>
      <p className={`mt-3 text-xs flex items-center gap-1.5 ${dark ? 'text-white/60' : 'text-neutral-500'} ${center ? 'justify-center' : 'justify-center md:justify-start'}`}>
        <ShieldCheck size={13} /> {t('We respect your privacy. Unsubscribe anytime.')}
      </p>
    </form>
  );
}

export default function NewsletterSignup() {
  const { toast } = useToast();
  const location = useLocation();
  const es = langFromPath(location.pathname) === 'es';
  const t = (s) => (es ? (ES[s] ?? s) : s);
  useSeo({
    canonicalPath: es ? '/newsletter' : '/en/newsletter',
    lang: es ? 'es' : 'en',
    twinPath: es ? '/en/newsletter' : '/newsletter',
    title: es ? 'Boletín | Yakaleo' : 'Newsletter | Yakaleo',
    description: es ? 'No te pierdas transmisiones especiales ni eventos exclusivos.' : 'Never miss a special broadcast or exclusive event.',
  });
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [shows, setShows] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [showsReady, setShowsReady] = useState(false);

  useEffect(() => {
    Promise.all([
      base44.entities.Show.list('sort_order', 20).catch(() => []),
      base44.entities.Host.list('sort_order', 20).catch(() => []),
    ]).then(([sh, h]) => {
      setShows((sh || []).filter((x) => x.artwork_url));
      setHosts((h || []).filter((x) => x.photo_url));
      setShowsReady(true);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await base44.entities.NewsletterSubscriber.create({ email });
      toast({ title: t('Subscribed!'), description: t("You're on the list.") });
      setDone(true);
      setEmail('');
    } catch (err) {
      toast({ title: t('Subscription failed'), description: err.message, variant: 'destructive' });
    }
    setSubmitting(false);
  };

  const formProps = { es, email, setEmail, submitting, done, onSubmit: submit, onReset: () => setDone(false), t };
  const perks = [
    { icon: Radio, title: 'Special Broadcasts', text: 'Be first to know about live shows and guest mixes.' },
    { icon: Calendar, title: 'Station News', text: 'Updates, contests, and station announcements.' },
    { icon: Bell, title: 'Exclusive Events', text: 'Invites to listener events and meetups.' },
  ];

  return (
    <div className="bg-neutral-100 min-h-screen">
      {/* Hero with the signup form above the fold */}
      <div className="relative overflow-hidden bg-black">
        <img src={STATION_HERO_IMAGE} srcSet={mediaSrcSet(STATION_HERO_IMAGE, [640, 1280, 1920]) || undefined} sizes="100vw" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-purple-950/40" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black to-transparent" />
        <div className="relative max-w-[1100px] mx-auto px-4 md:px-8 py-12 md:py-20 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div className="text-center md:text-left">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-fuchsia-200 text-[11px] font-bold tracking-[0.25em]">
              <Sparkles size={13} /> {es ? 'LA ESTACIÓN DEL BAILOTEO · BOLETÍN' : 'LA ESTACIÓN DEL BAILOTEO · NEWSLETTER'}
            </span>
            <h1 className="text-white text-4xl md:text-6xl font-black tracking-tight leading-[1.05] mt-5">
              {es ? (
                <>Que no te lo <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-purple-300">cuenten</span>.</>
              ) : (
                <>Hear it here <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-purple-300">first</span>.</>
              )}
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-4 max-w-md mx-auto md:mx-0">
              {es
                ? 'Shows especiales, mixes invitados, concursos y eventos de La Estación del Bailoteo, directo a tu correo.'
                : 'Special shows, guest mixes, contests and events from La Estación del Bailoteo, straight to your inbox.'}
            </p>
            <div className="mt-7 max-w-lg mx-auto md:mx-0">
              <SignupForm {...formProps} dark id="email-hero" />
            </div>
          </div>

          {/* Real show covers and DJs */}
          {shows.length > 0 && (
            <div className="hidden md:flex flex-col items-center" aria-hidden="true">
              <div className="relative w-full max-w-md h-[360px]">
                {shows.slice(0, 3).map((sh, i) => {
                  const pos = [
                    'left-0 top-10 -rotate-[8deg] z-10',
                    'left-1/2 -translate-x-1/2 top-0 z-30 scale-105',
                    'right-0 top-10 rotate-[8deg] z-20',
                  ][shows.length === 1 ? 1 : i];
                  return (
                    <div key={sh.id} className={`absolute w-56 aspect-square rounded-2xl overflow-hidden shadow-2xl shadow-black/70 ring-1 ring-white/20 ${pos}`}>
                      <img src={sh.artwork_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-3">
                        <p className="text-white text-sm font-black truncate">{sh.name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {hosts.length > 0 && (
                <div className="mt-2 flex items-center gap-3 rounded-full bg-black/50 backdrop-blur-md border border-white/15 pl-2 pr-4 py-2">
                  <div className="flex -space-x-3">
                    {hosts.slice(0, 4).map((h) => (
                      <img key={h.id} src={h.photo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-black" loading="lazy" />
                    ))}
                  </div>
                  <p className="text-white/80 text-xs font-bold">
                    {es ? 'Shows, DJs y eventos de Yakaleo' : 'Yakaleo shows, DJs and events'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Marquee of the station's shows (its height is held while the shows load) */}
      {!showsReady && <div className="h-[44px] md:h-[48px] bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-700" aria-hidden="true" />}
      {shows.length > 0 && (
        <div className="bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-700 overflow-hidden py-3" aria-hidden="true">
          <style>{`@keyframes ykMarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}@media (prefers-reduced-motion:reduce){.yk-marquee{animation:none!important}}`}</style>
          <div className="yk-marquee flex w-max gap-8 whitespace-nowrap" style={{ animation: 'ykMarquee 30s linear infinite' }}>
            {[0, 1].map((dup) => (
              <div key={dup} className="flex gap-8">
                {[...shows.map((sh) => sh.name), 'La Estación del Bailoteo', ...hosts.map((h) => h.name)].map((name, i) => (
                  <span key={`${dup}-${i}`} className="text-white font-black text-sm md:text-base tracking-widest uppercase flex items-center gap-8">
                    {name} <span className="text-white/50">✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs font-black tracking-[0.2em] text-purple-700 uppercase">{es ? 'Lo que recibes' : "What you'll get"}</span>
          <span className="flex-1 h-px bg-gradient-to-r from-neutral-300 to-transparent" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {perks.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white rounded-2xl shadow-sm p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
              <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white flex items-center justify-center shadow-lg shadow-purple-600/30">
                <Icon size={22} />
              </span>
              <h2 className="text-black text-lg font-black mt-4">{t(title)}</h2>
              <p className="text-neutral-600 text-sm mt-1">{t(text)}</p>
            </div>
          ))}
        </div>

        {/* Second chance to sign up for people who scrolled */}
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-purple-700 to-purple-950 p-6 md:p-10 text-center">
          <p className="text-white font-black text-2xl md:text-3xl leading-tight">{es ? '¿Listo para el bailoteo?' : 'Ready for the bailoteo?'}</p>
          <p className="text-white/75 text-sm md:text-base mt-2">{es ? 'Un email, cero spam.' : 'One email, zero spam.'}</p>
          <div className="mt-6 max-w-lg mx-auto">
            <SignupForm {...formProps} dark center id="email-bottom" />
          </div>
        </div>
      </div>
    </div>
  );
}